import React, { useEffect, useState } from 'react';
import OidcAuthService from '../services/oidcAuth';
import { isExternalUserError, checkForUrlAuthErrors } from '../utils/authErrorDetection';

// Login callback component
export const LoginCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Simple navigate function that doesn't rely on React Router
  const simpleNavigate = (path: string) => {
    window.location.href = path;
  };

  // Callback component mounted for processing

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔄 Starting OIDC callback processing...');
        console.log('🔗 Full URL:', window.location.href);
        console.log('🔗 Pathname:', window.location.pathname);
        console.log('🔗 Search params:', window.location.search);
        console.log('🔗 Hash:', window.location.hash);
        
        // First, check for URL-based auth errors
        if (checkForUrlAuthErrors()) {
          console.log('🚫 URL auth error detected, redirecting handled by checkForUrlAuthErrors');
          return;
        }
        
        // Check if this looks like an OIDC callback
        const hasCode = window.location.search.includes('code=') || window.location.hash.includes('code=');
        const hasState = window.location.search.includes('state=') || window.location.hash.includes('state=');
        
        console.log('🔍 Callback indicators:', { hasCode, hasState });
        
        if (!hasCode) {
          console.warn('⚠️ No authorization code found in URL - this might not be a valid callback');
        }
        
        const oidcService = OidcAuthService.getInstance();
        console.log('📞 Calling handleLoginCallback...');
        
        const user = await oidcService.handleLoginCallback();
        console.log('👤 User result:', user);
        
        if (user) {
          console.log('✅ Login successful! User:', {
            id: user.profile.sub,
            name: user.profile.name,
            email: user.profile.email
          });
          
          // Redirect to the intended page or dashboard
          const returnUrl = sessionStorage.getItem('oidc_return_url') || '/';
          console.log('🔄 Redirecting to:', returnUrl);
          sessionStorage.removeItem('oidc_return_url');
          
          // Store authentication success flag in localStorage for persistence
          localStorage.setItem('oidc_auth_success', 'true');
          localStorage.setItem('oidc_auth_timestamp', Date.now().toString());
          
          console.log('✅ Authentication successful, redirecting...');
          
          // Use location.replace for immediate redirect without delay
          window.location.replace(returnUrl);
        } else {
          console.error('❌ Login failed: No user returned from callback');
          setError('Login failed: No user returned from authentication service');
        }
      } catch (error) {
        console.error('❌ Login callback error:', error);
        
        // More detailed error information
        if (error instanceof Error) {
          console.error('Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
          
          // Check for external user access errors and redirect to custom error page
          if (isExternalUserError(error)) {
            console.log('🚫 External user access attempt detected in callback, redirecting...');
            window.location.href = '/auth-error?type=external_user';
            return;
          }
          
          setError(`Login failed: ${error.message}`);
        } else {
          console.error('Unknown error type:', typeof error, error);
          setError(`Login failed: ${String(error)}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Completing UniFirst Login...
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we securely log you in.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <h2 className="text-lg font-semibold mb-2">Login Error</h2>
            <p>{error}</p>
          </div>
          <button
            onClick={() => simpleNavigate('/')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Logout callback component
export const LogoutCallback: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Simple navigate function that doesn't rely on React Router
  const simpleNavigate = (path: string) => {
    window.location.href = path;
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔄 Starting logout callback processing...');
        console.log('🔗 Logout callback URL:', window.location.href);
        
        const oidcService = OidcAuthService.getInstance();
        await oidcService.handleLogoutCallback();
        
        console.log('✅ Logout successful, redirecting to home');
        
        // Ensure all auth state is cleared
        localStorage.removeItem('oidc_auth_success');
        localStorage.removeItem('oidc_auth_timestamp');
        
        // Small delay to ensure cleanup is complete
        setTimeout(() => {
          simpleNavigate('/');
        }, 500);
      } catch (error) {
        console.error('❌ Logout callback error:', error);
        
        // Even on error, try to clean up and redirect
        localStorage.clear();
        sessionStorage.clear();
        
        console.log('🔄 Logout error occurred, but clearing storage and redirecting anyway');
        
        setTimeout(() => {
          simpleNavigate('/');
        }, 1000);
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Logging Out...
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we securely log you out.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <h2 className="text-lg font-semibold mb-2">Logout Error</h2>
            <p>{error}</p>
          </div>
          <button
            onClick={() => simpleNavigate('/')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="text-green-600 text-5xl mb-4">✓</div>
        <h2 className="text-xl font-semibold text-gray-900">
          Logout Successful
        </h2>
        <p className="mt-2 text-gray-600">
          You have been successfully logged out. Redirecting...
        </p>
      </div>
    </div>
  );
};

// Silent callback component (for token renewal)
export const SilentCallback: React.FC = () => {
  useEffect(() => {
    const handleCallback = async () => {
      try {
        const oidcService = OidcAuthService.getInstance();
        await oidcService.handleSilentCallback();
        console.log('Silent callback completed successfully');
      } catch (error) {
        console.error('Silent callback error:', error);
      }
    };

    handleCallback();
  }, []);

  // This component should be minimal and not render anything visible
  return <div style={{ display: 'none' }}>Silent callback processing...</div>;
};
